import React from 'react';
import { Check, X } from 'lucide-react';

export type ConnectionPhase = 'finding' | 'connected' | 'failed' | 'wifi-failed' | 'wifi-connecting' | 'success';

interface Props {
  message: string;
  paused?: boolean;
  phase?: ConnectionPhase;
}

const DeviceConnectionStatus: React.FC<Props> = ({ message, paused, phase = 'finding' }) => {
  /* ── Phase 2: Hotspot connected — device with green checkmark badge ── */
  if (phase === 'connected') {
    return (
      <div className="dcf-content--center">
        <div className="dcf-conn-status">
          <div className="dcf-conn-status__ring dcf-conn-phase-connected">
            <svg viewBox="0 0 220 220" className="dcf-conn-status__ring-svg dcf-conn-status--static">
              <circle cx="110" cy="110" r="106" fill="none" stroke="#E5E7EB" strokeWidth="2" />
            </svg>
            <div className="dcf-conn-status__device">
              <picture>
                <source
                  srcSet={`${process.env.PUBLIC_URL}/DeviceSetup/device-mobile.avif`}
                  type="image/avif"
                />
                <img
                  srcSet={`${process.env.PUBLIC_URL}/DeviceSetup/device-mobile.webp`}
                  alt="SmartEco IAQ device"
                  className="dcf-illustration"
                />
              </picture>
            </div>
            <div className="dcf-conn-status__badge dcf-conn-status__badge--success" aria-hidden="true">
              <Check strokeWidth={3} />
            </div>
          </div>
          <p className="dcf-conn-status__message">{message}</p>
          <div className={`dcf-conn-status__dots${paused ? ' dcf-conn-status--paused' : ''}`} aria-hidden="true">
            <span className="dcf-conn-status__dot" />
            <span className="dcf-conn-status__dot" />
            <span className="dcf-conn-status__dot" />
          </div>
        </div>
      </div>
    );
  }

  /* ── Phase 2b: Failed — device with red cross badge ── */
  if (phase === 'failed') {
    return (
      <div className="dcf-content--center">
        <div className="dcf-conn-status">
          <div className="dcf-conn-status__ring dcf-conn-phase-connected">
            <svg viewBox="0 0 220 220" className="dcf-conn-status__ring-svg dcf-conn-status--static">
              <circle cx="110" cy="110" r="106" fill="none" stroke="#E5E7EB" strokeWidth="2" />
            </svg>
            <div className="dcf-conn-status__device">
              <picture>
                <source
                  srcSet={`${process.env.PUBLIC_URL}/DeviceSetup/device-mobile.avif`}
                  type="image/avif"
                />
                <img
                  srcSet={`${process.env.PUBLIC_URL}/DeviceSetup/device-mobile.webp`}
                  alt="SmartEco IAQ device"
                  className="dcf-illustration"
                />
              </picture>
            </div>
            <div className="dcf-conn-status__badge dcf-conn-status__badge--failed" aria-hidden="true">
              <X strokeWidth={3} />
            </div>
          </div>
          <p className="dcf-conn-status__message">{message}</p>
        </div>
      </div>
    );
  }

  /* ── Phase: WiFi send failed — red circle with X (mirrors success circle) ── */
  if (phase === 'wifi-failed') {
    return (
      <div className="dcf-content--center">
        <div className="dcf-conn-status dcf-conn-phase-wifi">
          <div className="dcf-conn-success-circle">
            <div className="dcf-conn-success-circle__ring dcf-conn-success-circle__ring--failed" />
            <div className="dcf-conn-success-circle__icon dcf-conn-success-circle__icon--failed">
              <X strokeWidth={2.5} />
            </div>
          </div>
          <p className="dcf-conn-status__message">{message}</p>
        </div>
      </div>
    );
  }

  /* ── Phase 4: Success — green circle with checkmark ── */
  if (phase === 'success') {
    return (
      <div className="dcf-content--center">
        <div className="dcf-conn-status dcf-conn-phase-wifi">
          <div className="dcf-conn-success-circle">
            <div className="dcf-conn-success-circle__ring" />
            <div className="dcf-conn-success-circle__icon">
              <Check strokeWidth={2.5} />
            </div>
          </div>
          <p className="dcf-conn-status__message dcf-conn-success__title">Connected</p>
          <p className="dcf-conn-status__message dcf-conn-success__subtitle">
            Please wait, we are setting up everything for you
          </p>
        </div>
      </div>
    );
  }

  /* ── Phase 3: WiFi connecting — WiFi icon with rotating ring ── */
  if (phase === 'wifi-connecting') {
    return (
      <div className="dcf-content--center">
        <div className="dcf-conn-status dcf-conn-phase-wifi">
          <div className="dcf-conn-wifi-ring">
            {/* Rotating arc */}
            <svg viewBox="0 0 120 120" className={`dcf-conn-wifi-ring__svg${paused ? ' dcf-conn-status--paused' : ''}`}>
              <circle cx="60" cy="60" r="56" fill="none" stroke="#E5E7EB" strokeWidth="2" />
              <circle
                cx="60"
                cy="60"
                r="56"
                fill="none"
                stroke="url(#wifi-conn-grad)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="110 242"
              />
              <defs>
                <linearGradient id="wifi-conn-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="var(--gradient-button-left-color, #0F796B)" />
                  <stop offset="100%" stopColor="var(--gradient-button-right-color, #6FDC95)" />
                </linearGradient>
              </defs>
            </svg>

            {/* WiFi icon — centered */}
            <div className="dcf-conn-wifi-ring__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12.55a11 11 0 0 1 14.08 0" />
                <path d="M1.42 9a16 16 0 0 1 21.16 0" />
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                <circle cx="12" cy="20" r="1" fill="currentColor" stroke="none" />
              </svg>
            </div>
          </div>

          <p className="dcf-conn-status__message dcf-conn-wifi__message">{message}</p>

          <p className="dcf-conn-status__note">
            Your device is almost set! Sit tight while your device connects to the internet.
          </p>
        </div>
      </div>
    );
  }

  /* ── Phase 1: Finding device — device image with rotating ring (default) ── */
  return (
    <div className="dcf-content--center">
      <div className="dcf-conn-status">
        {/* Animated ring around the device image */}
        <div className="dcf-conn-status__ring">
          <svg viewBox="0 0 220 220" className={`dcf-conn-status__ring-svg${paused ? ' dcf-conn-status--paused' : ''}`}>
            <circle
              cx="110"
              cy="110"
              r="106"
              fill="none"
              stroke="#E5E7EB"
              strokeWidth="2"
            />
            <circle
              cx="110"
              cy="110"
              r="106"
              fill="none"
              stroke="url(#conn-grad)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="200 466"
              className="dcf-conn-status__arc"
            />
            <defs>
              <linearGradient id="conn-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="var(--gradient-button-left-color, #0F796B)" />
                <stop offset="100%" stopColor="var(--gradient-button-right-color, #6FDC95)" />
              </linearGradient>
            </defs>
          </svg>

          {/* Device image — floats up and down */}
          <div className={`dcf-conn-status__device${paused ? '' : ' dcf-conn-status__device--float'}`}>
            <picture>
              <source
                srcSet={`${process.env.PUBLIC_URL}/DeviceSetup/device-mobile.avif`}
                type="image/avif"
              />
              <img
                srcSet={`${process.env.PUBLIC_URL}/DeviceSetup/device-mobile.webp`}
                alt="SmartEco IAQ device"
                className="dcf-illustration"
              />
            </picture>
          </div>
        </div>

        {/* Status message */}
        <p className="dcf-conn-status__message">{message}</p>

        {/* Animated dots */}
        <div className={`dcf-conn-status__dots${paused ? ' dcf-conn-status--paused' : ''}`} aria-hidden="true">
          <span className="dcf-conn-status__dot" />
          <span className="dcf-conn-status__dot" />
          <span className="dcf-conn-status__dot" />
        </div>

        {/* Hold-tight note */}
        <p className="dcf-conn-status__note">
          Hold tight — please keep this screen open and don&apos;t minimize or close the app. This may take 1–2 minutes.
        </p>
      </div>
    </div>
  );
};

export default DeviceConnectionStatus;
